import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Grid, Chip, alpha, IconButton, Divider
} from '@mui/material';
import { IonIcon } from '@ionic/react';
import { close, pricetag, calculator, calendar } from 'ionicons/icons';
import { Tax } from '../../../models/tax';

interface TaxDetailModalProps {
    open: boolean;
    onClose: () => void;
    tax: Tax | null;
    onEdit: () => void;
}

const TaxDetailModal: React.FC<TaxDetailModalProps> = ({ open, onClose, tax, onEdit }) => {
    if (!tax) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(139, 92, 246, 0.2)'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                p: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IonIcon icon={pricetag} style={{ fontSize: '2rem' }} />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {tax.name}
                            </Typography>
                            <Chip
                                label={tax.tax_code}
                                size="small"
                                sx={{
                                    mt: 1,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontFamily: 'monospace'
                                }}
                            />
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <IonIcon icon={close} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                {/* Rate and Date Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            color: 'white'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IonIcon icon={calculator} style={{ fontSize: '2rem' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Tax Rate
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        {Number(tax.rate).toFixed(2)}%
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            color: 'white'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IonIcon icon={calendar} style={{ fontSize: '2rem' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Created On
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {new Date(tax.created_at).toLocaleDateString('en-NG', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Tax Type */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155' }}>
                        Tax Type
                    </Typography>
                    <Chip
                        label={tax.tax_type}
                        sx={{
                            bgcolor: alpha('#0066cc', 0.1),
                            color: '#0066cc',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            px: 1
                        }}
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Tax ID */}
                <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                        Tax ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', fontFamily: 'monospace' }}>
                        {tax.geacloud_taxes_id || 'N/A'}
                    </Typography>
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                <Button
                    onClick={onClose}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        color: '#64748b',
                        '&:hover': {
                            bgcolor: '#f1f5f9'
                        }
                    }}
                >
                    Close
                </Button>
                <Button
                    onClick={onEdit}
                    variant="contained"
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                        fontWeight: 600,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                            boxShadow: '0 6px 16px rgba(139, 92, 246, 0.4)'
                        }
                    }}
                >
                    Edit Tax
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaxDetailModal;
